var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useRef, useEffect, useLayoutEffect, isValidElement, useContext, useCallback, } from 'react';
import classnames from 'classnames';
import { Icons } from '@beeline/design-tokens/js/iconfont';
import { useBEM, PopupsContext } from '../../utils';
import { TextField } from '../TextField';
import { DropdownMenu } from '../DropdownMenu';
import { IconButton } from '../IconButton';
import "./styles.css";
var _a = useBEM('lk-ui__select'), selectClassName = _a[0], _selectModifier = _a[1], selectElement = _a[2];
var _b = selectElement('options'), optionsClassName = _b[0], _optionsModifier = _b[1], optionsElement = _b[2];
var itemClassName = optionsElement('item')[0];
export var Select = function (_a) {
    var _b;
    var label = _a.label, helperText = _a.helperText, multiple = _a.multiple, size = _a.size, error = _a.error, values = _a.values, renderValue = _a.renderValue, onChange = _a.onChange, defaultValue = _a.defaultValue, onClose = _a.onClose, onOpen = _a.onOpen, open = _a.open, children = _a.children, onFocus = _a.onFocus, onBlur = _a.onBlur, onSubmit = _a.onSubmit, onClick = _a.onClick, options = _a.options, fullWidth = _a.fullWidth, className = _a.className, disabled = _a.disabled, makeOption = _a.makeOption, loading = _a.loading, loadingText = _a.loadingText, applicationRootElementID = _a.applicationRootElementID, dropdownElementID = _a.dropdownElementID, dropdownClassName = _a.dropdownClassName, props = __rest(_a, ["label", "helperText", "multiple", "size", "error", "values", "renderValue", "onChange", "defaultValue", "onClose", "onOpen", "open", "children", "onFocus", "onBlur", "onSubmit", "onClick", "options", "fullWidth", "className", "disabled", "makeOption", "loading", "loadingText", "applicationRootElementID", "dropdownElementID", "dropdownClassName"]);
    var containerRef = useRef(null);
    var optionsRef = useRef(null);
    var switchButtonRef = useRef(null);
    var rootElement = useContext(PopupsContext).rootElement;
    var _c = useState(open), isOpen = _c[0], setOpen = _c[1];
    var _d = useState((_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth), dropdownWidth = _d[0], setDropdownWidth = _d[1];
    useLayoutEffect(function () {
        var _a;
        setDropdownWidth((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth);
    });
    var defaultRenderValue = function (values) { return values.join(', '); };
    var inputValue = renderValue
        ? renderValue(values)
        : defaultRenderValue(values);
    var handleOpen = function () {
        setOpen(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    };
    var handleClose = function () {
        setOpen(false);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    };
    var onSelectItem = function (item) { return function () {
        onChange([item]);
        handleClose();
    }; };
    var onMultiselectItem = function (item) { return function () {
        var itemIndex = values.findIndex(function (value) { return item.id === value.id; });
        var isAddition = itemIndex === -1;
        var updItems = isAddition
            ? __spreadArray(__spreadArray([], values, true), [item], false) : values.filter(function (_, i) { return itemIndex !== i; });
        onChange(updItems);
    }; };
    var handleInputFocus = function (event) {
        if (!isOpen)
            handleOpen();
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    };
    var handleInputBlur = function (event) {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    };
    useEffect(function () {
        var handleOutsideClick = function (event) {
            var targetElement = event === null || event === void 0 ? void 0 : event.target;
            var inputElement = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
            var optionsElement = optionsRef === null || optionsRef === void 0 ? void 0 : optionsRef.current;
            var buttonElement = switchButtonRef === null || switchButtonRef === void 0 ? void 0 : switchButtonRef.current;
            var isOutsideClick = ![
                inputElement,
                optionsElement,
                buttonElement,
            ].some(function (element) { return element && element.contains(targetElement); });
            if (isOutsideClick) {
                handleClose();
            }
        };
        var listenerContainer = rootElement || window;
        listenerContainer.addEventListener('mousedown', handleOutsideClick);
        return function () {
            return listenerContainer.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);
    var makeItem = function (item, isSelected) {
        var className = classnames(itemClassName, {
            selected: isSelected,
        });
        var onClickHandler = multiple ? onMultiselectItem : onSelectItem;
        return (React.createElement("div", { className: className, onClick: onClickHandler(item), key: item.id.toString() }, makeOption(item, isSelected)));
    };
    var makeOptions = function (selectedItems, options) {
        return (React.createElement("div", { className: optionsClassName, ref: optionsRef, style: { width: dropdownWidth } }, loading ? (isValidElement(loadingText) ? (loadingText) : (React.createElement("span", null, loadingText || 'Загрузка'))) : (options.map(function (item) {
            return makeItem(item, selectedItems.some(function (i) { return i.id === item.id; }));
        }))));
    };
    var optionsComponents = makeOptions(values, options);
    var dropdownMenuProps = {
        targetElement: containerRef,
        align: 'center',
        verticalOffset: 0,
        applicationRootElementID: applicationRootElementID,
        dropdownElementID: dropdownElementID,
        className: dropdownClassName,
    };
    var rightIcon = (React.createElement(IconButton, { ref: switchButtonRef, size: size, iconName: isOpen ? Icons.NavArrowUp : Icons.NavArrowDown, onClick: isOpen ? handleClose : handleOpen, disabled: disabled }));
    var rootClassName = classnames(selectClassName, className);
    var handleTextFieldOnChange = useCallback(function () { }, []);
    return (React.createElement("div", { className: rootClassName },
        React.createElement(TextField, __assign({ containerRef: containerRef, label: label, helperText: helperText, size: size, error: error, fullWidth: fullWidth, endAdornment: rightIcon, onBlur: handleInputBlur, onFocus: handleInputFocus, disabled: disabled, value: inputValue, onChange: handleTextFieldOnChange, contentEditable: true, readOnly: true }, props)),
        isOpen && (React.createElement(DropdownMenu, __assign({}, dropdownMenuProps), optionsComponents))));
};
